import React, { useState, useCallback } from "react";
import Modal from "react-modal";
import {
  X,
  Download,
  Eye,
  FileText,
  FileArchive,
  File as FileIcon,
} from "lucide-react";
import "../styles/components/FilePreview.css";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
const ARCHIVE_EXTENSIONS = ["zip", "rar", "7z", "tar", "gz"];

const getFileMeta = (url) => {
  if (!url || typeof url !== "string") {
    return { name: "", extension: "", kind: "other" };
  }

  const cleanUrl = url.split("?")[0].split("#")[0];
  const rawName = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);

  let name = rawName;
  try {
    name = decodeURIComponent(rawName);
  } catch (e) {
    name = rawName;
  }

  const extIndex = name.lastIndexOf(".");
  const extension =
    extIndex !== -1 ? name.slice(extIndex + 1).toLowerCase() : "";

  let kind = "other";
  if (IMAGE_EXTENSIONS.includes(extension)) kind = "image";
  else if (extension === "pdf") kind = "pdf";
  else if (ARCHIVE_EXTENSIONS.includes(extension)) kind = "archive";

  return { name, extension, kind };
};

const truncateName = (name, maxLength = 26) => {
  if (!name || name.length <= maxLength) return name;
  const extIndex = name.lastIndexOf(".");
  const ext = extIndex !== -1 ? name.slice(extIndex) : "";
  const baseLength = maxLength - ext.length - 3;
  if (baseLength <= 0) return `...${ext}`;
  return `${name.slice(0, baseLength)}...${ext}`;
};

const FilePreview = ({ url, fileName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const meta = getFileMeta(url);
  const displayName = fileName || meta.name || "File";

  const handleDownload = useCallback(
    async (e) => {
      if (e) e.stopPropagation();
      if (!url) return;
      setDownloading(true);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = displayName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } catch (err) {
        // S3 cross-origin fetch can be blocked; fall back to opening the file.
        window.open(url, "_blank", "noopener,noreferrer");
      } finally {
        setDownloading(false);
      }
    },
    [url, displayName],
  );

  if (!url || typeof url !== "string") {
    return <span className="file-preview-empty">-</span>;
  }

  const canPreview = meta.kind === "image" || meta.kind === "pdf";
  const openPreview = () => setIsOpen(true);
  const closePreview = () => setIsOpen(false);

  const renderTypeIcon = (size) => {
    if (meta.kind === "pdf")
      return <FileText size={size} className="file-preview-icon pdf" />;
    if (meta.kind === "archive")
      return <FileArchive size={size} className="file-preview-icon archive" />;
    return <FileIcon size={size} className="file-preview-icon" />;
  };

  const renderThumbnail = () => {
    if (meta.kind === "image") {
      return (
        <img
          src={url}
          alt={displayName}
          className="file-preview-thumb"
          loading="lazy"
        />
      );
    }
    return (
      <span className="file-preview-thumb file-preview-thumb--icon">
        {renderTypeIcon(22)}
      </span>
    );
  };

  const renderModalBody = () => {
    if (meta.kind === "image") {
      return (
        <img
          src={url}
          alt={displayName}
          className="file-preview-modal-image"
        />
      );
    }
    if (meta.kind === "pdf") {
      return (
        <iframe
          src={url}
          title={displayName}
          className="file-preview-modal-frame"
        />
      );
    }
    return (
      <div className="file-preview-noview">
        {renderTypeIcon(64)}
        <p>Preview isn't available for this file type.</p>
        <button
          type="button"
          className="file-preview-download-btn"
          onClick={handleDownload}
          disabled={downloading}
        >
          <Download size={16} />
          {downloading ? "Downloading..." : "Download file"}
        </button>
      </div>
    );
  };

  return (
    <div className="file-preview">
      <button
        type="button"
        className="file-preview-trigger"
        onClick={openPreview}
        title="Click to open"
      >
        {renderThumbnail()}
        <span className="file-preview-name">{truncateName(displayName)}</span>
      </button>

      <div className="file-preview-actions">
        {canPreview && (
          <button
            type="button"
            className="file-preview-action"
            onClick={openPreview}
            title="Preview"
          >
            <Eye size={16} />
          </button>
        )}
        <button
          type="button"
          className="file-preview-action"
          onClick={handleDownload}
          disabled={downloading}
          title="Download"
        >
          <Download size={16} />
        </button>
      </div>

      <Modal
        isOpen={isOpen}
        onRequestClose={closePreview}
        className="file-preview-modal"
        overlayClassName="file-preview-overlay"
        ariaHideApp={false}
      >
        <div className="file-preview-modal-header">
          <span className="file-preview-modal-title" title={displayName}>
            {displayName}
          </span>
          <div className="file-preview-modal-tools">
            <button
              type="button"
              className="file-preview-action"
              onClick={handleDownload}
              disabled={downloading}
              title="Download"
            >
              <Download size={18} />
            </button>
            <button
              type="button"
              className="file-preview-action"
              onClick={closePreview}
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="file-preview-modal-body">{renderModalBody()}</div>
      </Modal>
    </div>
  );
};

export default FilePreview;
